/**
 * Confirmation, keyed by real visit id. The product has no confirmation concept,
 * so it lives here. The round itself is in `confirmationRound.ts`; this module
 * keeps the annotation plumbing and the two rules that outlive any one screen:
 *
 * - **Moved after the text.** A visit that has been texted — Pending, Confirmed
 *   or already Needs a call — and whose clinician or start then changes through
 *   any path at all is Needs a call and carries *Moved after the text*. The move
 *   is **added** to whatever the row already said, never substituted for it, and
 *   a visit moved twice carries it once.
 * - **One text, wherever the visit came from.** A visit that lands on a day whose
 *   round has already gone is texted on its own the moment it lands, if the
 *   patient consents. Nobody who already answered is texted again.
 */
import type { DateKey, DemoState, IsoString, ServerSnapshot, VisitAnnotation, VisitResourceLike } from "@caredna-marlin/utils/homeHealthDemo/state";
import { anchorsFor, hasTextConsent, movedAfterText, movedToAnotherDay, roundFired, roundLabel, withMove } from "./confirmationRound";
import type { TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";
import { toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";

export function annotationFor(state: Pick<DemoState, "annotations">, realVisitId: number): VisitAnnotation | undefined {
  return state.annotations[String(realVisitId)];
}

export function ensureAnnotation(annotations: Record<string, VisitAnnotation>, vr: VisitResourceLike, origin: VisitAnnotation["origin"] = "census"): VisitAnnotation {
  return (
    annotations[String(vr.visitId)] ?? {
      realVisitId: vr.visitId,
      origin,
      confirmation: "notSent",
      lastSeenProviderId: vr.providerId,
      lastSeenStartIso: vr.startIso,
    }
  );
}

/** Visits (one resource per visit) on a day, excluding canceled. */
export function visitsOnDay(snapshot: ServerSnapshot, dateKey: DateKey, timeZone: string): VisitResourceLike[] {
  const seen = new Set<number>();
  return snapshot.visitResources.filter((vr) => {
    if (vr.eventStatus === "canceled" || seen.has(vr.visitId)) return false;
    if (toDateKey(new Date(vr.startIso), timeZone) !== dateKey) return false;
    seen.add(vr.visitId);
    return true;
  });
}

export interface ResetResult {
  annotations: Record<string, VisitAnnotation>;
  resetVisitIds: number[];
}

/**
 * Compare the previous annotations with the snapshot that just landed.
 *
 * Every **texted** visit whose clinician or start moved — not only a confirmed
 * one, as this did until 2026-09-19 — becomes Needs a call and gains *Moved
 * after the text*, even when the move came through the product's own
 * drag-and-drop, Optimize, or a presenter event. `lastSeen*` is stamped with
 * where the visit now is, so the reason is written once however often it moves.
 *
 * A visit that lands on a day whose round has already gone is **not** texted
 * (Pete, 2026-09-25): nobody has looked at the hour a drag or a rebook gave it.
 * It reads Not sent, which is what happened.
 */
export function deriveConfirmationResets(state: Pick<DemoState, "annotations" | "config" | "confirmationRounds" | "meta" | "patients">, next: ServerSnapshot, nowIso?: IsoString): ResetResult {
  const timeZone = state.config.branch.timeZone;
  const annotations = state.annotations;
  const out = { ...annotations };
  const resetVisitIds: number[] = [];
  const byVisit = new Map<number, VisitResourceLike>();
  for (const vr of next.visitResources) if (!byVisit.has(vr.visitId)) byVisit.set(vr.visitId, vr);

  for (const [key, a] of Object.entries(annotations)) {
    const vr = byVisit.get(a.realVisitId);
    if (!vr) continue;
    const providerChanged = a.lastSeenProviderId !== undefined && vr.providerId !== a.lastSeenProviderId;
    const startChanged = a.lastSeenStartIso !== undefined && vr.startIso !== a.lastSeenStartIso;
    if (!providerChanged && !startChanged) continue;
    if (a.round && movedToAnotherDay(a, vr, timeZone)) {
      // **Another day voids the telling.** Whatever the patient answered was
      // about a day this visit is no longer on, so the round record goes rather
      // than travelling with it, and the card says nothing about texts again.
      const { round: _voided, ...withoutRound } = a;
      out[key] = { ...withoutRound, lastSeenProviderId: vr.providerId, lastSeenStartIso: vr.startIso };
      resetVisitIds.push(a.realVisitId);
    } else if (a.round && movedAfterText(a, vr)) {
      out[key] = {
        ...a,
        round: withMove(a.round),
        lastSeenProviderId: vr.providerId,
        lastSeenStartIso: vr.startIso,
      };
      resetVisitIds.push(a.realVisitId);
    } else {
      out[key] = { ...a, lastSeenProviderId: vr.providerId, lastSeenStartIso: vr.startIso };
    }
  }

  return { annotations: out, resetVisitIds };
}

/**
 * What the chip says, wherever a visit appears: the round's own four states, and
 * "Not sent" for a visit the round has not reached.
 */
export function confirmationLabel(state: Pick<DemoState, "config">, a: VisitAnnotation | undefined): TimeSentence {
  return roundLabel(state, a?.round);
}

/** Colour family for the chip: green once it is confirmed, amber while it waits, red when it needs the clinician. */
export function confirmationTone(a: VisitAnnotation | undefined): "confirmed" | "waiting" | "needsAttention" | "none" {
  switch (a?.round?.status) {
    case "confirmed":
      return "confirmed";
    case "needsCall":
      return "needsAttention";
    case "pending":
      return "waiting";
    default:
      return "none";
  }
}
